import { getTranslations } from "@/lib/translations";
import { RedirectError } from "@/lib/errors";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";
import { z } from "zod";

import { canUserPublishContent, requireAdmin, requireStaff } from "@/core/auth/admin-guard";
import { createArticleService } from "@/core/services/factory";
import {
  ARTICLE_COVER_IMAGE_MAX_BYTES,
  isAcceptedCoverImageType,
} from "@/core/services/types/media.types";
import {
  ARTICLE_EDIT_STATUS_VALUES,
  getArticleSharePath,
  getArticleContentPublishFieldErrors,
  isArticleEditorialChecklistComplete,
  isArticlePublishStatus,
  isArticleScheduleStatus,
  isValidSchedulePublishDate,
  parseDateInputValue,
  type UpdateArticleState,
  type UpdateArticleTaxonomyResult,
} from "@/core/services/types/article.types";

function parseFieldErrors(
  issues: z.ZodIssue[],
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !fieldErrors[field]) {
      fieldErrors[field] = issue.message;
    }
  }
  return fieldErrors;
}

function normalizeRichText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "<p></p>") return null;
  return value;
}

function normalizeOptionalText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function mapCoverImageUploadError(
  error: string,
  t: ReturnType<typeof getTranslations>,
): string {
  const normalized = error.toLowerCase();

  if (normalized.includes("row-level security")) {
    return t("coverImageStorageForbidden");
  }

  if (normalized.includes("bucket not found")) {
    return t("coverImageBucketNotFound");
  }

  return t("coverImageUploadFailed");
}

export async function updateArticleContent(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));
  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  const articles = createArticleService();
  const existing = await articles.getForEdit(idResult.data);

  if (!existing) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }

  const isPublished = existing.status === "PUBLISHED";

  const schema = z.object({
    id: z.uuid(),
    title: isPublished
      ? z.string().trim().min(1, t("titleRequired"))
      : z.preprocess(
          (value) => (typeof value === "string" ? value : ""),
          z.string(),
        ),
    sub_title: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    content_rich_text: z.preprocess(
      normalizeRichText,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    title: formData.get("title"),
    sub_title: formData.get("sub_title"),
    content_rich_text: formData.get("content_rich_text"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, ...content } = parsed.data;

  if (isPublished) {
    const publishFieldErrors = getArticleContentPublishFieldErrors(content, {
      titleRequired: t("titleRequired"),
      subTitleRequired: t("subTitleRequired"),
      contentRequired: t("contentRequired"),
    });

    if (Object.keys(publishFieldErrors).length > 0) {
      return {
        success: false,
        error: t("publishedContentIncomplete"),
        fieldErrors: publishFieldErrors,
      };
    }
  }

  const updated = await articles.updateArticleContent(id, content);

  if (!updated) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }
  return { success: true, data: updated };
}

function normalizeOptionalUuid(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export async function updateArticleStatus(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  const user = await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    edit_status: z.enum(ARTICLE_EDIT_STATUS_VALUES),
    published_at: z.preprocess(
      (value) => (typeof value === "string" ? value : ""),
      z.string(),
    ).optional(),
    publish_immediately: z.preprocess(
      (value) => value === "true",
      z.boolean().optional(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    edit_status: formData.get("edit_status"),
    published_at: formData.get("published_at") ?? undefined,
    publish_immediately: formData.get("publish_immediately") ?? undefined,
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, edit_status, published_at, publish_immediately } = parsed.data;

  const requiresPublishPermission =
    isArticlePublishStatus(edit_status) || isArticleScheduleStatus(edit_status);

  if (requiresPublishPermission && !canUserPublishContent(user)) {
    return {
      success: false,
      error: t("publishForbidden"),
    };
  }

  const articles = createArticleService();

  const requiresChecklist =
    isArticlePublishStatus(edit_status) || isArticleScheduleStatus(edit_status);

  if (requiresChecklist) {
    const article = await articles.getForEdit(id);

    if (!article || !isArticleEditorialChecklistComplete(article)) {
      return {
        success: false,
        error: t("publishChecklistIncomplete"),
      };
    }
  }

  let schedulePublishAt: string | null | undefined;
  if (isArticleScheduleStatus(edit_status)) {
    schedulePublishAt = parseDateInputValue(published_at ?? "");
    if (!schedulePublishAt || !isValidSchedulePublishDate(schedulePublishAt)) {
      return {
        success: false,
        error: t("scheduledPublishAtInvalid"),
        fieldErrors: {
          published_at: t("scheduledPublishAtInvalid"),
        },
      };
    }
  }

  const updated = await articles.updateArticleStatus(id, {
    editStatus: edit_status,
    schedulePublishAt,
    publishImmediately: publish_immediately === true,
  });

  if (!updated) {
    return {
      success: false,
      error: t("statusUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateArticleAssignment(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    assigned_to: z.preprocess(
      normalizeOptionalUuid,
      z.string().uuid().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    assigned_to: formData.get("assigned_to"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, assigned_to } = parsed.data;
  const articles = createArticleService();
  const updated = await articles.updateArticleAssignment(id, {
    assignedTo: assigned_to,
  });

  if (!updated) {
    return {
      success: false,
      error: t("assignmentUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateArticleInternalNotes(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    internal_notes: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    internal_notes: formData.get("internal_notes"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, internal_notes } = parsed.data;
  const articles = createArticleService();
  const updated = await articles.updateArticleInternalNotes(id, {
    internalNotes: internal_notes,
  });

  if (!updated) {
    return {
      success: false,
      error: t("internalNotesUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateArticleCoverImage(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    cover_image_url: z.preprocess(
      (value) =>
        typeof value === "string" && value.trim() === "" ? null : value,
      z.url(t("invalidCoverImageUrl")).nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    cover_image_url: formData.get("cover_image_url"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, cover_image_url } = parsed.data;
  const articles = createArticleService();
  const updated = await articles.updateArticleCoverImage(id, {
    coverImageUrl: cover_image_url,
  });

  if (!updated) {
    return {
      success: false,
      error: t("coverImageUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function uploadArticleCoverImage(
  _prevState: UpdateArticleState,
  formData: FormData,
): Promise<UpdateArticleState> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");
  const idResult = z.uuid().safeParse(formData.get("id"));
  const file = formData.get("cover_image");

  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  if (!(file instanceof File) || file.size === 0) {
    return {
      success: false,
      error: t("coverImageRequired"),
    };
  }

  if (!isAcceptedCoverImageType(file.type)) {
    return {
      success: false,
      error: t("coverImageInvalidType"),
    };
  }

  if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
    return {
      success: false,
      error: t("coverImageTooLarge"),
    };
  }

  const articles = createArticleService();
  const result = await articles.uploadArticleCoverImage(idResult.data, file);

  if (!result.success) {
    return {
      success: false,
      error: mapCoverImageUploadError(result.error, t),
    };
  }
  return { success: true, data: result.data };
}

export async function cancelArticleCreationAction(id: string): Promise<void> {
  await requireStaff();

  const t = getTranslations("articles.edit");
  const parsed = z.uuid().safeParse(id);

  if (!parsed.success) {
    throw new Error(t("cancelCreationFailed"));
  }

  const result = await createArticleService().cancelArticleCreation(parsed.data);

  if (!result.success) {
    throw new Error(
      result.error === "not_cancellable"
        ? t("cancelCreationNotAllowed")
        : t("cancelCreationFailed"),
    );
  }

  throw new RedirectError(getLibraryReturnHref("articles"));
}

export async function deleteArticleSignalAction(id: string): Promise<void> {
  await requireAdmin();

  const t = getTranslations("articles.edit.statusActions");
  const parsed = z.uuid().safeParse(id);

  if (!parsed.success) {
    throw new Error(t("deleteFailed"));
  }

  const result = await createArticleService().deleteSignalContent(parsed.data);

  if (!result.success) {
    throw new Error(
      result.error === "not_deletable" ? t("deleteNotAllowed") : t("deleteFailed"),
    );
  }
}

export async function updateArticleTaxonomy(
  _prevState: UpdateArticleTaxonomyResult,
  formData: FormData,
): Promise<UpdateArticleTaxonomyResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));

  if (!idResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const uuidArray = z.array(z.uuid());

  const artFormIds = uuidArray.safeParse(formData.getAll("art_form_ids"));
  const artisticPeriodIds = uuidArray.safeParse(
    formData.getAll("artistic_period_ids"),
  );
  const culturalAreaIds = uuidArray.safeParse(
    formData.getAll("cultural_area_ids"),
  );
  const exhibitionTypeIds = uuidArray.safeParse(
    formData.getAll("exhibition_type_ids"),
  );

  if (
    !artFormIds.success ||
    !artisticPeriodIds.success ||
    !culturalAreaIds.success ||
    !exhibitionTypeIds.success
  ) {
    return { success: false, error: t("fixFields") };
  }

  const articles = createArticleService();
  const result = await articles.updateArticleTaxonomy(idResult.data, {
    artFormIds: artFormIds.data,
    artisticPeriodIds: artisticPeriodIds.data,
    culturalAreaIds: culturalAreaIds.data,
    exhibitionTypeIds: exhibitionTypeIds.data,
  });

  if (!result.success) {
    return { success: false, error: t("taxonomyUpdateFailed") };
  }
  return { success: true };
}
